// BrimLight/client/helpers/video-editor/VideoPreviewer.js

import { ComponentBase } from "../../core/ComponentBase.js";
import { KenBurnsAnimator } from "./KenBurnsAnimator.js";
import { TextAnimator } from "./TextAnimator.js";

export class VideoPreviewer extends ComponentBase {
    static styles = `
        .video-previewer-container {
            position: relative;
            /* Let the aspect-ratio property do the work */
            max-width: 100%;
            max-height: 100%;
            margin: 0 auto;
            background-color: #000;
            overflow: hidden;
            border-radius: var(--bs-border-radius);
            transition: all 0.3s ease;
        }
        
        /* Fullscreen mode */
        .video-previewer-container.fullscreen {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100vw !important;
            height: 100vh !important;
            max-width: 100vw !important;
            max-height: 100vh !important;
            z-index: 9999;
            border-radius: 0;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        /* Fullscreen toggle button */
        .previewer-fullscreen-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 10;
            background-color: rgba(0, 0, 0, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            backdrop-filter: blur(4px);
            opacity: 0;
            pointer-events: none;
        }
        
        .video-previewer-container:hover .previewer-fullscreen-btn,
        .previewer-fullscreen-btn:hover {
            opacity: 1;
            pointer-events: auto;
        }
        
        .previewer-fullscreen-btn:hover {
            background-color: rgba(0, 0, 0, 0.8);
            transform: scale(1.1);
        }
        
        .previewer-fullscreen-btn i {
            font-size: 1.2rem;
        }
        
        .preview-canvas {
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
        }

        .preview-layers-container {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 5; 
            pointer-events: none;
        }
        
        .preview-image-wrapper {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            /* --- START OF FIX for transition glitch --- */
            /* Hint to the browser to optimize this element for animations */
            will-change: transform, opacity;
            /* --- END OF FIX for transition glitch --- */
        }
        
        .preview-image {
            width: auto;
            height: auto;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            transform-origin: center center;
        }

        video.preview-image {
            width: 100%;
            height: 100%;
        }
        
        .preview-image.pan-effect {
            min-width: 130%;
            min-height: 130%;
            object-fit: cover;
        }
        
        .transition-out { 
            animation-duration: 1s; 
            animation-fill-mode: forwards; 
            z-index: 1; 
        }
        .transition-in { 
            animation-duration: 1s; 
            animation-fill-mode: forwards; 
            z-index: 2; 
        }

        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes fade-from-white { from { filter: brightness(10); opacity: 0; } to { filter: brightness(1); opacity: 1; } }
        @keyframes slide-in-from-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slide-out-to-left { from { transform: translateX(0); } to { transform: translateX(-100%); } }
        @keyframes slide-in-from-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes slide-out-to-right { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @keyframes slide-in-from-bottom { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes slide-out-to-top { from { transform: translateY(0); } to { transform: translateY(-100%); } }
        @keyframes slide-in-from-top { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        @keyframes slide-out-to-bottom { from { transform: translateY(0); } to { transform: translateY(100%); } }
        @keyframes wipe-in-from-left { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
        @keyframes wipe-in-from-right { from { clip-path: inset(0 0 0 100%); } to { clip-path: inset(0 0 0 0); } }
        @keyframes wipe-in-from-top { from { clip-path: inset(100% 0 0 0); } to { clip-path: inset(0 0 0 0); } }
        @keyframes wipe-in-from-bottom { from { clip-path: inset(0 0 100% 0); } to { clip-path: inset(0 0 0 0); } }
        @keyframes zoom-in-effect { from { transform: scale(1.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes zoom-out-effect { from { transform: scale(1); opacity: 1; } to { transform: scale(0.5); opacity: 0; } }

        .crossfade-out, .dip-to-black-out, .dip-to-white-out { animation-name: fade-out; }
        .crossfade-in, .dip-to-black-in { animation-name: fade-in; }
        .dip-to-white-in { animation-name: fade-from-white; }
        .slide-left-out { animation-name: slide-out-to-left; }
        .slide-left-in { animation-name: slide-in-from-right; }
        .slide-right-out { animation-name: slide-out-to-right; }
        .slide-right-in { animation-name: slide-in-from-left; }
        .slide-up-out { animation-name: slide-out-to-top; }
        .slide-up-in { animation-name: slide-in-from-bottom; }
        .slide-down-out { animation-name: slide-out-to-bottom; }
        .slide-down-in { animation-name: slide-in-from-top; }
        .wipe-left-in { animation-name: wipe-in-from-left; }
        .wipe-right-in { animation-name: wipe-in-from-right; }
        .wipe-up-in { animation-name: wipe-in-from-top; }
        .wipe-down-in { animation-name: wipe-in-from-bottom; }
        .wipe-left-out, .wipe-right-out, .wipe-up-out, .wipe-down-out { animation-name: none; }
        .zoom-in-out { animation-name: fade-out; }
        .zoom-in-in { animation-name: zoom-in-effect; }
        .zoom-out-out { animation-name: zoom-out-effect; }
        .zoom-out-in { animation-name: fade-in; }
    `;


    constructor(config) {
        super(config);
        this.projectSettings = null;
        this.clips = [];
        this.activeVisualClip = null;
        this.audioElements = new Map();
        this.currentAnimation = null;
        this.isFullscreen = false;
        this.animators = new Map();
        this.isPlaying = false;
        this.activeVideoElement = null;
        this.layersContainer = null;
        this.resizeObserver = null; // --- NEW: Property for the observer

        this.applyStyles();
    }


    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'video-previewer-container';
        this.container.innerHTML = `
            <div class="preview-canvas">
                <div class="preview-layers-container"></div>
            </div>
            <button class="previewer-fullscreen-btn" title="Toggle Fullscreen">
                <i class="bi bi-arrows-fullscreen"></i>
            </button>
        `;
        this.canvas = this.container.querySelector('.preview-canvas');
        this.layersContainer = this.container.querySelector('.preview-layers-container');
        this.fullscreenBtn = this.container.querySelector('.previewer-fullscreen-btn');

        this.fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isFullscreen) {
                this.toggleFullscreen();
            }
        });

        // --- START OF FIX for font scaling ---
        // Setup the ResizeObserver to automatically handle font scaling.
        this.resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const displayWidth = entry.contentRect.width;
                if (displayWidth > 0) {
                    const baseFontSize = displayWidth * 0.018;
                    this.container.style.setProperty('--preview-font-size-base', `${baseFontSize}px`);
                }
            }
        });
        this.resizeObserver.observe(this.container);
        // --- END OF FIX for font scaling ---

        this.animators.set('text', new TextAnimator(this.layersContainer, this.appClient));

        await super.initialize();
    }

    render() {
        if (this.projectSettings) {
            const [width, height] = this.projectSettings.aspectRatio.split(':').map(Number);

            if (this.isFullscreen) {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const viewportAspect = viewportWidth / viewportHeight;
                const contentAspect = width / height;
                let displayWidth, displayHeight;

                if (viewportAspect > contentAspect) {
                    displayHeight = viewportHeight;
                    displayWidth = displayHeight * contentAspect;
                } else {
                    displayWidth = viewportWidth;
                    displayHeight = displayWidth / contentAspect;
                }

                this.container.style.width = displayWidth + 'px';
                this.container.style.height = displayHeight + 'px';
            } else {
                this.container.style.aspectRatio = `${width} / ${height}`;
                this.container.style.height = '100%';
                this.container.style.width = 'auto';
                this.container.style.maxWidth = '100%';
            }
        }
        // NOTE: Font scaling is now completely handled by the ResizeObserver,
        // so we no longer need to calculate it here.
        return this.container;
    }



    renderNewVisual() {
        const outgoingWrapper = this.canvas.querySelector('.preview-image-wrapper:not(.transition-out)');

        if (this.currentAnimation) {
            KenBurnsAnimator.cancelAnimation(this.currentAnimation);
            this.currentAnimation = null;
        }

        let incomingWrapper = null;

        if (this.activeVisualClip && this.activeVisualClip.videoAssetPath) {
            // It's a video clip
            this.activeVideoElement = null; // Reset first
            incomingWrapper = document.createElement('div');
            incomingWrapper.className = 'preview-image-wrapper';

            const videoElement = document.createElement('video');
            videoElement.className = 'preview-image'; // Use same class for sizing
            videoElement.muted = true; // Audio is handled by separate tracks
            videoElement.loop = true; // Loop for simplicity, controlled by updateVisuals

            const cacheBuster = this.activeVisualClip.assetHash || Date.now();
            const videoSrc = this.activeVisualClip.videoAssetPath.startsWith('/')
                ? `${this.activeVisualClip.videoAssetPath}?v=${cacheBuster}`
                : `/${this.activeVisualClip.videoAssetPath}?v=${cacheBuster}`;
            videoElement.src = videoSrc;

            this.activeVideoElement = videoElement; // Store reference

            incomingWrapper.appendChild(videoElement);
            // FIX: Insert before the layers container to ensure layers are on top
            this.canvas.insertBefore(incomingWrapper, this.layersContainer);

        } else if (this.activeVisualClip && this.activeVisualClip.assetPath) {
            // It's an image clip (existing logic)
            this.activeVideoElement = null; // Ensure no video is active
            incomingWrapper = document.createElement('div');
            incomingWrapper.className = 'preview-image-wrapper';

            const incomingElement = document.createElement('img');
            incomingElement.className = 'preview-image';

            const cacheBuster = this.activeVisualClip.assetHash || Date.now();
            const imgSrc = this.activeVisualClip.assetPath.startsWith('/')
                ? `${this.activeVisualClip.assetPath}?v=${cacheBuster}`
                : `/${this.activeVisualClip.assetPath}?v=${cacheBuster}`;
            incomingElement.src = imgSrc;

            incomingWrapper.appendChild(incomingElement);
            // FIX: Insert before the layers container to ensure layers are on top
            this.canvas.insertBefore(incomingWrapper, this.layersContainer);

            const imageEffect = this.activeVisualClip.imageEffect || 'ken_burns_zoom_in';
            const clipDuration = this.activeVisualClip.duration;

            incomingElement.onload = () => {
                this.currentAnimation = KenBurnsAnimator.applyEffect(incomingElement, imageEffect, clipDuration);
            };
        }

        const transitionEffect = this.activeVisualClip?.transitionEffect || 'cut';

        if (outgoingWrapper && transitionEffect !== 'cut') {
            const transitionClass = transitionEffect.replace(/_/g, '-');
            outgoingWrapper.classList.add('transition-out', `${transitionClass}-out`);
            if (incomingWrapper) {
                incomingWrapper.classList.add('transition-in', `${transitionClass}-in`);
            }
            setTimeout(() => outgoingWrapper.remove(), 1000);
        } else if (outgoingWrapper) {
            outgoingWrapper.remove();
        }
    }


    toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;

        if (this.isFullscreen) {
            this.container.classList.add('fullscreen');
            this.fullscreenBtn.querySelector('i').className = 'bi bi-fullscreen-exit';
            this.fullscreenBtn.title = 'Exit Fullscreen (ESC)';
        } else {
            this.container.classList.remove('fullscreen');
            this.fullscreenBtn.querySelector('i').className = 'bi bi-arrows-fullscreen';
            this.fullscreenBtn.title = 'Toggle Fullscreen';
        }

        // Re-run the entire render logic, which now includes the reliable font scaling.
        this.render();
    }

    setData(clips, projectSettings) {
        this.clips = clips || [];
        this.projectSettings = projectSettings;
        // Re-run the entire render logic on new data.
        this.render();
    }



    update(currentTime, isPlaying = false) {
        if (!this.clips) return;
        this.isPlaying = isPlaying;

        // 1. Find all active clips
        const activeVisualClips = this.clips.filter(c => c.type === 'visual' && currentTime >= c.startTime && currentTime < (c.startTime + c.duration));
        const activeLayerClips = this.clips.filter(c => c.type === 'layer' && currentTime >= c.startTime && currentTime < (c.startTime + c.duration));

        // 2. Handle the main visual track (background image/video)
        const newVisualClip = activeVisualClips[0];
        if (newVisualClip?._id !== this.activeVisualClip?._id) {
            this.activeVisualClip = newVisualClip;
            this.renderNewVisual();
        }

        // 3. Manage playback and state for the CURRENT active visual
        if (this.activeVideoElement && this.activeVisualClip) {
            // It's a video, manage its playback time and state
            const clipAge = currentTime - this.activeVisualClip.startTime;
            if (Math.abs(this.activeVideoElement.currentTime - clipAge) > 0.2) {
                this.activeVideoElement.currentTime = clipAge;
            }
            if (this.isPlaying && this.activeVideoElement.paused) {
                this.activeVideoElement.play().catch(e => this.log.warn('Video play failed:', e.message));
            } else if (!this.isPlaying && !this.activeVideoElement.paused) {
                this.activeVideoElement.pause();
            }
        } else if (this.activeVisualClip && this.activeVisualClip.assetPath && !this.activeVideoElement) {
            // --- START OF FIX for lost Ken Burns animation ---
            // It's an image. Check if its animation was lost (e.g., after fullscreen toggle).
            if (!this.currentAnimation) {
                const imageElement = this.canvas.querySelector('.preview-image-wrapper:not(.transition-out) .preview-image');
                if (imageElement) {
                    // The element exists on screen, but our animation reference is gone. Re-apply it.
                    this.log.info('Re-applying lost Ken Burns animation.');
                    const imageEffect = this.activeVisualClip.imageEffect || 'ken_burns_zoom_in';
                    const clipDuration = this.activeVisualClip.duration;
                    this.currentAnimation = KenBurnsAnimator.applyEffect(imageElement, imageEffect, clipDuration);
                }
            }
            // --- END OF FIX ---
        }

        // 4. Update all animators (like TextAnimator) with their active clips
        const clipsByType = new Map();
        activeLayerClips.forEach(clip => {
            const type = clip.layerType;
            if (!clipsByType.has(type)) {
                clipsByType.set(type, []);
            }
            clipsByType.get(type).push(clip);
        });

        this.animators.forEach((animator, type) => {
            animator.update(clipsByType.get(type) || [], currentTime);
        });

        // 5. Manage audio playback
        this.manageAudioPlayback(currentTime, isPlaying);
    }

    updateFontScaling() {
        if (!this.container) return;
        const displayWidth = this.container.clientWidth;
        if (displayWidth > 0) {
            // Base the font size on 1.8% of the previewer's current width.
            const baseFontSize = displayWidth * 0.018;
            this.container.style.setProperty('--preview-font-size-base', `${baseFontSize}px`);
        }
    }


    updateVisuals(currentTime) {
        const newVisualClip = this.clips.find(c =>
            c.type === 'visual' &&
            currentTime >= c.startTime &&
            currentTime < (c.startTime + c.duration)
        );

        if (newVisualClip?._id !== this.activeVisualClip?._id) {
            this.activeVisualClip = newVisualClip;
            this.renderNewVisual();
        }

        // NEW: Add video playback management
        if (this.activeVideoElement && this.activeVisualClip) {
            const clipAge = currentTime - this.activeVisualClip.startTime;
            // Seek if the video's current time is significantly different from the timeline's time
            if (Math.abs(this.activeVideoElement.currentTime - clipAge) > 0.2) {
                this.activeVideoElement.currentTime = clipAge;
            }

            if (this.isPlaying && this.activeVideoElement.paused) {
                this.activeVideoElement.play().catch(e => this.log.warn('Video play failed:', e.message));
            } else if (!this.isPlaying && !this.activeVideoElement.paused) {
                this.activeVideoElement.pause();
            }
        }
    }

    updateLayers(currentTime) {
        const activeLayerClips = this.clips.filter(c =>
            c.type === 'layer' &&
            currentTime >= c.startTime &&
            currentTime < (c.startTime + c.duration)
        );

        const clipsByType = new Map();
        activeLayerClips.forEach(clip => {
            const type = clip.layerType;
            if (!clipsByType.has(type)) {
                clipsByType.set(type, []);
            }
            clipsByType.get(type).push(clip);
        });

        this.animators.forEach((animator, type) => {
            animator.update(clipsByType.get(type) || [], currentTime);
        });
    }

    manageAudioPlayback(currentTime, isPlaying) {
        const allAudioClips = this.clips.filter(c =>
            ['dialogue', 'sfx', 'bgMusic', 'ambient'].includes(c.type) && c.assetPath
        );

        for (const clip of allAudioClips) {
            let audio = this.audioElements.get(clip._id);
            if (!audio) {
                let audioSrc = clip.assetPath;
                if (!audioSrc.startsWith('http')) {
                    if (audioSrc.startsWith('global/')) {
                        audioSrc = `/media/${audioSrc}`;
                    }
                    else if (!audioSrc.startsWith('/')) {
                        if (!audioSrc.startsWith('media/')) {
                            audioSrc = `/media/${audioSrc}`;
                        }
                        else {
                            audioSrc = `/${audioSrc}`;
                        }
                    }
                }
                audio = new Audio(audioSrc);
                audio.loop = ['bgMusic', 'ambient', 'sfx'].includes(clip.type);
                this.audioElements.set(clip._id, audio);
            }

            const trackSettings = this.projectSettings?.trackSettings?.[clip.type] || {};
            const volume = trackSettings.volume ?? 1;
            const muted = trackSettings.muted ?? false;

            // --- START OF FIX: Add a safeguard to clamp the volume ---
            // This ensures the value is always within the valid [0, 1] range for the HTMLMediaElement.
            const clampedVolume = Math.max(0, Math.min(1, volume));
            audio.volume = muted ? 0 : clampedVolume;
            // --- END OF FIX ---

            const isTimeWithinClip = currentTime >= clip.startTime && currentTime < (clip.startTime + clip.duration);
            const playPosition = currentTime - clip.startTime;
            const isLooping = audio.loop;

            if (isTimeWithinClip) {
                let seekTime = playPosition;
                if (isLooping && audio.duration > 0) {
                    seekTime = playPosition % audio.duration;
                }

                if (isPlaying && audio.paused) {
                    if (!isLooping && seekTime >= audio.duration) {
                        // Do nothing for non-looping audio that has finished.
                    } else {
                        audio.currentTime = seekTime;
                        audio.play().catch(e => this.log.warn(`Audio play failed for ${clip._id}:`, e.message));
                    }
                } else if (!isPlaying) {
                    if (!audio.paused) audio.pause();
                    if (Math.abs(audio.currentTime - seekTime) > 0.2) {
                        audio.currentTime = seekTime;
                    }
                }
            } else {
                if (!audio.paused) {
                    audio.pause();
                    audio.currentTime = 0;
                }
            }
        }
    }


    stopAllAudio() {
        this.audioElements.forEach((audio) => {
            if (!audio.paused) {
                audio.pause();
                audio.currentTime = 0;
            }
        });
    }


    destroy() {
        // --- NEW: Disconnect the observer to prevent memory leaks ---
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.currentAnimation) {
            KenBurnsAnimator.cancelAnimation(this.currentAnimation);
        }
        this.stopAllAudio();
        this.audioElements.clear();
        this.animators.forEach(animator => animator.destroy());
        this.animators.clear();
        super.destroy();
    }

}